import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import { Link } from 'react-router-dom';
import { GitBranch,
  Cable,
  ChevronDown,
  ChevronRight,
  Flame,
  Gauge,
  List as ListIcon,
  MapPin,
  MoveDown,
  MoveRight,
  Network as NetworkIcon,
  Pencil,
  Plus,
  Radio,
  Route,
  Search,
  Split,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { clipBoundary,
  cutOut,
  distanceMetres,
  pathLengthMetres,
  redrawWithNeighbours,
  shapeOf,
  splitZone,
  type CutOutResponse,
  type FibreRoute,
  type LatLng,
  type NeighbourMove,
  type SplitResponse,
  type ZoneShape,
  FIBRE_DB_PER_KM,
  MAX_TOPOLOGY_DEPTH,
  SPLITTER_CAPACITIES,
  SPLITTER_KIND_LABELS,
  SPLITTER_LOSS_AMBER_DB,
  cascadeLossDb,
  fibreLossDb,
  formatDistance,
  upstreamCapacities,
  type Olt,
  type OpticalSplitter,
  type PonPort,
  type Splitter,
  type SplitterKind,
  type SplitterOnus,
  type Topology,
  type Zone,
} from '@simpleacs/shared';
import { CascadeWizard } from './CascadeWizard';
import { ZoneTools } from './ZoneImportExport';
import {
  ConfirmDeleteDialog,
  OpticalSplitterDialog,
  upstreamSummary,
} from './OpticalSplitterDialog';
import { sortedHousings } from './Splitters';
import { api } from '../lib/api';
import { onuPath } from '../lib/links';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import { MIN_RING_CORNERS, deleteVertex, insertVertex, midpoints, moveVertex } from '../lib/zone-vertices';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
} from '../components/ui';

type ViewMode = 'tree' | 'lr' | 'tb';
type CreateSpec = { kind: 'zone' | 'splitter'; parentId: string | null; zoneId?: string | null };
type EditSpec = { kind: 'zone'; zone: Zone } | { kind: 'splitter'; splitter: Splitter };
type DeleteSpec = { kind: 'zone'; zone: Zone } | { kind: 'splitter'; splitter: Splitter };

/** Ancestor count per node — a node at MAX_TOPOLOGY_DEPTH cannot take children. */
function depthMap(nodes: Array<{ id: string; parentId: string | null }>): Map<string, number> {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  const memo = new Map<string, number>();
  for (const node of nodes) {
    let depth = 0;
    let cursor = byId.get(node.id);
    while (cursor?.parentId && depth <= MAX_TOPOLOGY_DEPTH + 2) {
      cursor = byId.get(cursor.parentId);
      depth += 1;
    }
    memo.set(node.id, depth);
  }
  return memo;
}

/**
 * Physical topology: zones and splitter cascades (list or node graph), with
 * the map below. The heatmap weights splitter positions by the ONUs hanging
 * off them, so deployment clusters are visible at a glance.
 */
export function TopologyPage() {
  const { can } = useSession();
  const [creating, setCreating] = useState<CreateSpec | null>(null);
  const [buildingCascade, setBuildingCascade] = useState(false);
  const [drawingZoneId, setDrawingZoneId] = useState('');
  // Laying a fibre route and drawing a boundary are the same clicks — never both.
  const [drawingRoute, setDrawingRoute] = useState(false);
  const [cascadeNotice, setCascadeNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditSpec | null>(null);
  const [managing, setManaging] = useState<Splitter | null>(null);
  const [deleting, setDeleting] = useState<DeleteSpec | null>(null);
  const [deviceDialog, setDeviceDialog] = useState<{
    existing: OpticalSplitter | null;
    housingId: string | null;
  } | null>(null);
  const [deviceDeleting, setDeviceDeleting] = useState<OpticalSplitter | null>(null);
  const queryClient = useQueryClient();
  const [heatOn, setHeatOn] = useState(true);
  const [view, setView] = useState<ViewMode>('tree');

  const topologyQuery = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });
  const oltsQuery = useQuery({
    queryKey: ['olts'],
    queryFn: () => api.get<{ items: Olt[] }>('/olts'),
  });

  const topology = topologyQuery.data;
  const oltNames = useMemo(
    () => new Map((oltsQuery.data?.items ?? []).map((o) => [o.id, o.name])),
    [oltsQuery.data],
  );
  const placed = useMemo(
    () =>
      [
        ...(topology?.zones ?? []).filter((z) => z.lat !== null),
        ...(topology?.splitters ?? []).filter((s) => s.lat !== null),
      ].length,
    [topology],
  );

  return (
    <>
      <PageHeader
        title="Topology"
        description="Zones, enclosures and splitters — and where the fleet actually sits."
        actions={
          can('topology.manage') ? (
            <div className="flex gap-2">
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setCreating({ kind: 'zone', parentId: null })}
              >
                New zone
              </Button>
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setCreating({ kind: 'splitter', parentId: null, zoneId: null })}
              >
                New splitter
              </Button>
              <Button
                variant="primary"
                icon={<GitBranch className="size-4" />}
                onClick={() => setBuildingCascade(true)}
              >
                Build a cascade
              </Button>
            </div>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Zones"
          value={topology?.zones.length ?? 0}
          icon={<MapPin className="size-4" />}
        />
        <StatCard
          label="Splitters"
          value={topology?.splitters.length ?? 0}
          icon={<NetworkIcon className="size-4" />}
        />
        <StatCard label="Placed on the map" value={placed} />
        <StatCard
          label="Heat points"
          value={topology?.heat.length ?? 0}
          tone={topology?.heat.length ? 'success' : 'default'}
        />
      </div>

      {topologyQuery.isLoading ? (
        <div className="flex justify-center p-16">
          <Spinner className="size-6" />
        </div>
      ) : topologyQuery.error ? (
        <div className="mt-5">
          <ErrorNotice error={topologyQuery.error} />
        </div>
      ) : topology ? (
        <div className="mt-5 space-y-5">
          <StructureCard
            topology={topology}
            oltNames={oltNames}
            view={view}
            onView={setView}
            onCreate={setCreating}
            onEdit={setEditing}
            onManage={setManaging}
            onDelete={setDeleting}
            onCreateDevice={(housingId) => setDeviceDialog({ existing: null, housingId })}
            onEditDevice={(d) => setDeviceDialog({ existing: d, housingId: d.housingId })}
            onDeleteDevice={setDeviceDeleting}
          />
          <Card
            title="Map"
            action={
              <span className="flex flex-wrap items-center gap-3">
                {can('topology.manage') && (
                  <span className="flex items-center gap-1.5">
                    <select
                      className="input h-7 w-40 text-xs"
                      value={drawingZoneId}
                      onChange={(e) => {
                        setDrawingRoute(false);
                        setDrawingZoneId(e.target.value);
                      }}
                      title="Draw or redraw a zone's boundary on the map"
                    >
                      <option value="">Draw a boundary…</option>
                      {topology.zones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name}
                          {z.boundary ? ' (redraw)' : ''}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant={drawingRoute ? 'primary' : 'ghost'}
                      icon={<Route className="size-3.5" />}
                      title="Draw a fibre route: click the splitter the cable leaves, lay its bends, click the splitter it reaches — the length feeds the loss readout"
                      onClick={() => {
                        setDrawingZoneId('');
                        setDrawingRoute((v) => !v);
                      }}
                    >
                      {drawingRoute ? 'Drawing a route…' : 'Fibre route'}
                    </Button>
                  </span>
                )}
                <ZoneTools canManage={can('topology.manage')} />
                <label className="flex cursor-pointer items-center gap-2 text-xs text-muted">
                  <input type="checkbox" checked={heatOn} onChange={(e) => setHeatOn(e.target.checked)} />
                  <Flame className="size-3.5" />
                  Deployment heatmap
                </label>
              </span>
            }
          >
            {/* What sits in no zone at all — the number Auto-assign is measured against. */}
            <p className="border-b border-line px-5 py-2 text-xs text-muted">
              {(topology.unplaced?.onus ?? 0).toLocaleString()} ONU{(topology.unplaced?.onus ?? 0) === 1 ? '' : 's'} and{' '}
              {(topology.unplaced?.splitters ?? 0).toLocaleString()} splitter{(topology.unplaced?.splitters ?? 0) === 1 ? '' : 's'} not in any zone
              {(topology.unplaced?.onus ?? 0) + (topology.unplaced?.splitters ?? 0) > 0 && can('topology.manage')
                ? ' — Auto-assign places the ones standing inside a drawn boundary.'
                : '.'}
            </p>
            <TopologyMap
              topology={topology}
              olts={oltsQuery.data?.items ?? []}
              heatOn={heatOn}
              drawingZoneId={drawingZoneId || null}
              onDrawExit={() => setDrawingZoneId('')}
              drawingRoute={drawingRoute}
              onRouteExit={() => setDrawingRoute(false)}
              canManage={can('topology.manage')}
            />
            <p className="border-t border-line px-5 py-2 text-[11px] text-faint">
              Streets © OpenStreetMap contributors · Satellite © Esri. Heat weights each splitter
              by the ONUs assigned to it. Fibre routes are labelled with their measured length;
              the loss readouts count them at {FIBRE_DB_PER_KM} dB/km.
            </p>
          </Card>
        </div>
      ) : null}

      {cascadeNotice && (
        <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 rounded-lg border border-line bg-surface px-4 py-2 text-sm text-ink shadow-lg">
          {cascadeNotice}
        </div>
      )}
      {buildingCascade && topology && (
        <CascadeWizard
          topology={topology}
          olts={oltsQuery.data?.items ?? []}
          onClose={() => setBuildingCascade(false)}
          onDone={(message) => {
            setBuildingCascade(false);
            setCascadeNotice(message);
            setTimeout(() => setCascadeNotice(null), 8000);
          }}
        />
      )}
      {creating && (
        <UpsertDialog
          kind={creating.kind}
          parentId={creating.parentId}
          zoneId={creating.zoneId ?? null}
          existing={null}
          zones={topology?.zones ?? []}
          splitters={topology?.splitters ?? []}
          olts={oltsQuery.data?.items ?? []}
          onClose={() => setCreating(null)}
        />
      )}
      {editing && (
        <UpsertDialog
          kind={editing.kind}
          parentId={editing.kind === 'zone' ? editing.zone.parentId : editing.splitter.parentId}
          zoneId={editing.kind === 'splitter' ? editing.splitter.zoneId : null}
          existing={editing.kind === 'zone' ? editing.zone : editing.splitter}
          zones={topology?.zones ?? []}
          splitters={topology?.splitters ?? []}
          olts={oltsQuery.data?.items ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {managing && <OnusDialog splitter={managing} onClose={() => setManaging(null)} />}
      {deleting && <DeleteDialog spec={deleting} onClose={() => setDeleting(null)} />}
      {deviceDialog && topology && (
        <OpticalSplitterDialog
          existing={deviceDialog.existing}
          defaultHousingId={deviceDialog.housingId}
          housings={sortedHousings(topology.splitters)}
          devices={topology.opticalSplitters ?? []}
          onClose={() => setDeviceDialog(null)}
        />
      )}
      {deviceDeleting && (
        <ConfirmDeleteDialog
          title={`Delete ${deviceDeleting.name}?`}
          body={
            <p>
              The registry entry is removed — nothing on the chassis changes. A splitter other
              devices are spliced to is refused by name.
            </p>
          }
          onConfirm={async () => {
            await api.delete(`/optical-splitters/${encodeURIComponent(deviceDeleting.id)}`);
            await queryClient.invalidateQueries({ queryKey: ['topology'] });
          }}
          onClose={() => setDeviceDeleting(null)}
        />
      )}
    </>
  );
}

// --- structure card ----------------------------------------------------------

const VIEWS: Array<{ mode: ViewMode; title: string; icon: typeof ListIcon }> = [
  { mode: 'tree', title: 'Tree list', icon: ListIcon },
  { mode: 'lr', title: 'Nodes, left to right', icon: MoveRight },
  { mode: 'tb', title: 'Nodes, top to bottom', icon: MoveDown },
];

function StructureCard({
  topology,
  oltNames,
  view,
  onView,
  onCreate,
  onEdit,
  onManage,
  onDelete,
  onCreateDevice,
  onEditDevice,
  onDeleteDevice,
}: {
  topology: Topology;
  oltNames: Map<string, string>;
  view: ViewMode;
  onView: (v: ViewMode) => void;
  onCreate: (c: CreateSpec) => void;
  onEdit: (e: EditSpec) => void;
  onManage: (s: Splitter) => void;
  onDelete: (d: DeleteSpec) => void;
  onCreateDevice: (housingId: string) => void;
  onEditDevice: (d: OpticalSplitter) => void;
  onDeleteDevice: (d: OpticalSplitter) => void;
}) {
  const mains = topology.zones.filter((z) => !z.parentId);
  const unzoned = topology.splitters.filter((s) => !s.zoneId && !s.parentId);
  const zoneDepths = useMemo(() => depthMap(topology.zones), [topology.zones]);
  const splitterDepths = useMemo(() => depthMap(topology.splitters), [topology.splitters]);

  const ctx: NodeContext = {
    topology,
    oltNames,
    zoneDepths,
    splitterDepths,
    onCreate,
    onEdit,
    onManage,
    onDelete,
    onCreateDevice,
    onEditDevice,
    onDeleteDevice,
  };

  return (
    <Card
      title="Zones & splitters"
      action={
        <div className="flex rounded-lg border border-line p-0.5">
          {VIEWS.map(({ mode, title, icon: Icon }) => (
            <button
              key={mode}
              type="button"
              title={title}
              onClick={() => onView(mode)}
              className={`rounded-md px-2 py-1 transition-colors ${
                view === mode ? 'bg-surface-2 text-ink' : 'text-faint hover:text-muted'
              }`}
            >
              <Icon className="size-3.5" />
            </button>
          ))}
        </div>
      }
    >
      {mains.length === 0 && unzoned.length === 0 ? (
        <EmptyState
          icon={<MapPin className="size-8" />}
          title="Nothing modelled yet"
          description="Create a zone for each service area, add its splitters, then assign ONUs on their device pages."
        />
      ) : view === 'tree' ? (
        <div className="divide-y divide-line">
          {mains.map((zone) => (
            <ZoneRow key={zone.id} zone={zone} ctx={ctx} depth={0} />
          ))}
          {unzoned.map((splitter) => (
            <SplitterRow key={splitter.id} splitter={splitter} ctx={ctx} depth={0} />
          ))}
        </div>
      ) : (
        <>
          <GraphLegend />
          <div className={`overflow-x-auto px-5 py-6 ${view === 'lr' ? 'graph-lr' : 'graph-tb'}`}>
            <ul
              className={
                view === 'lr'
                  ? 'flex min-w-max flex-col gap-6'
                  : 'flex min-w-max items-start justify-center gap-10'
              }
            >
              {mains.map((zone) => (
                <GraphZoneNode key={zone.id} zone={zone} ctx={ctx} />
              ))}
              {unzoned.map((splitter) => (
                <GraphSplitterNode key={splitter.id} splitter={splitter} ctx={ctx} />
              ))}
            </ul>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * What the graph's shorthand means, above the graph rather than in a tooltip.
 *
 * The dB figure is the one that needs saying out loud: it is a planning number
 * for the splitters alone, and reading it as a link budget would flatter every
 * cascade on the page by however much fibre it actually runs over.
 */
function GraphLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-line px-5 py-2.5 text-[11px] text-muted">
      <span className="flex items-center gap-1.5">
        <span className="flex size-4 items-center justify-center rounded bg-primary-soft text-primary">
          <MapPin className="size-2.5" />
        </span>
        Zone
      </span>
      <span className="flex items-center gap-1.5">
        <span className="flex size-4 items-center justify-center rounded bg-info-soft text-info">
          <NetworkIcon className="size-2.5" />
        </span>
        Splitter
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-1 w-8 overflow-hidden rounded-full bg-surface-2">
          <span className="block h-full w-1/2 rounded-full bg-success" />
        </span>
        ports used
        <span className="text-faint">· amber past 80%, red when full</span>
      </span>
      <span className="flex items-center gap-1.5">
        <Gauge className="size-3" />
        <span className="text-faint">
          dB is the typical insertion loss of that splitter and every one above it — fibre,
          splices and connectors are on top
        </span>
      </span>
    </div>
  );
}

interface NodeContext {
  topology: Topology;
  oltNames: Map<string, string>;
  zoneDepths: Map<string, number>;
  splitterDepths: Map<string, number>;
  onCreate: (c: CreateSpec) => void;
  onEdit: (e: EditSpec) => void;
  onManage: (s: Splitter) => void;
  onDelete: (d: DeleteSpec) => void;
  /** The physical devices housed inside tree stages — same registry as /splitters. */
  onCreateDevice: (housingId: string) => void;
  onEditDevice: (d: OpticalSplitter) => void;
  onDeleteDevice: (d: OpticalSplitter) => void;
}

function ZoneActions({ zone, ctx }: { zone: Zone; ctx: NodeContext }) {
  const { can } = useSession();
  if (!can('topology.manage')) return null;
  const canNest = (ctx.zoneDepths.get(zone.id) ?? 0) < MAX_TOPOLOGY_DEPTH;
  return (
    <span className="flex gap-1">
      {canNest && (
        <Button
          size="sm"
          variant="ghost"
          title="Add sub-zone"
          icon={<Plus className="size-3.5" />}
          onClick={() => ctx.onCreate({ kind: 'zone', parentId: zone.id })}
        />
      )}
      <Button
        size="sm"
        variant="ghost"
        title="Add splitter here"
        icon={<NetworkIcon className="size-3.5" />}
        onClick={() => ctx.onCreate({ kind: 'splitter', parentId: null, zoneId: zone.id })}
      />
      <Button
        size="sm"
        variant="ghost"
        title="Edit zone"
        icon={<Pencil className="size-3.5" />}
        onClick={() => ctx.onEdit({ kind: 'zone', zone })}
      />
      <Button
        size="sm"
        variant="ghost"
        title="Delete zone"
        icon={<Trash2 className="size-3.5" />}
        onClick={() => ctx.onDelete({ kind: 'zone', zone })}
      />
    </span>
  );
}

function SplitterActions({ splitter, ctx }: { splitter: Splitter; ctx: NodeContext }) {
  const { can } = useSession();
  if (!can('topology.manage')) return null;
  const canNest = (ctx.splitterDepths.get(splitter.id) ?? 0) < MAX_TOPOLOGY_DEPTH;
  return (
    <span className="flex gap-1">
      {canNest && (
        <Button
          size="sm"
          variant="ghost"
          title="Add sub-splitter"
          icon={<Plus className="size-3.5" />}
          onClick={() =>
            ctx.onCreate({ kind: 'splitter', parentId: splitter.id, zoneId: splitter.zoneId })
          }
        />
      )}
      <Button
        size="sm"
        variant="ghost"
        title="Add a splitter device inside this housing"
        icon={<Split className="size-3.5" />}
        onClick={() => ctx.onCreateDevice(splitter.id)}
      />
      <Button
        size="sm"
        variant="ghost"
        title="ONUs on this splitter"
        icon={<Radio className="size-3.5" />}
        onClick={() => ctx.onManage(splitter)}
      />
      <Button
        size="sm"
        variant="ghost"
        title="Edit splitter"
        icon={<Pencil className="size-3.5" />}
        onClick={() => ctx.onEdit({ kind: 'splitter', splitter })}
      />
      <Button
        size="sm"
        variant="ghost"
        title="Delete splitter"
        icon={<Trash2 className="size-3.5" />}
        onClick={() => ctx.onDelete({ kind: 'splitter', splitter })}
      />
    </span>
  );
}

/** "OLT-name · gpon_1/2/1" for a main splitter attached to a PON port. */
function feedLabel(splitter: Splitter, oltNames: Map<string, string>): string | null {
  if (!splitter.oltId && !splitter.oltPonPort) return null;
  const name = splitter.oltId ? (oltNames.get(splitter.oltId) ?? 'OLT') : 'OLT';
  return splitter.oltPonPort ? `${name} · ${splitter.oltPonPort}` : name;
}

/**
 * "feed: 1.2 km · +0.4 dB" — the fibre measured from the top of the chain
 * down to this splitter, from the routes drawn on the map (2026-09-16), and
 * what it costs at FIBRE_DB_PER_KM. Null when a hop above is not drawn: a
 * partial figure would flatter the budget, so nothing is shown rather than
 * something short.
 */
function feedFibre(splitter: Splitter): { text: string; title: string } | null {
  if (splitter.feedLengthM === null || splitter.feedLengthM === undefined) return null;
  const loss = splitter.feedLossDb ?? fibreLossDb(splitter.feedLengthM);
  return {
    text: `feed: ${formatDistance(splitter.feedLengthM)} · +${loss.toFixed(1)} dB`,
    title: `${formatDistance(splitter.feedLengthM)} of fibre measured from the top of the chain down to here along the routes drawn, costing +${loss.toFixed(1)} dB at ${FIBRE_DB_PER_KM} dB/km (G.652 at 1310 nm, the upstream wavelength)`,
  };
}

// --- tree list view ----------------------------------------------------------

function ZoneRow({ zone, ctx, depth }: { zone: Zone; ctx: NodeContext; depth: number }) {
  const [open, setOpen] = useState(depth === 0);
  const subZones = ctx.topology.zones.filter((z) => z.parentId === zone.id);
  const zoneSplitters = ctx.topology.splitters.filter((s) => s.zoneId === zone.id && !s.parentId);

  return (
    <div>
      <div
        className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2"
        style={{ paddingLeft: `${16 + depth * 20}px` }}
      >
        <button onClick={() => setOpen((v) => !v)} className="text-faint">
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <MapPin className="size-4 text-primary" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{zone.name}</span>
        {zone.lat === null && <Pill tone="warn">not placed</Pill>}
        <span className="text-xs text-faint">
          {zone.splitterCount ?? 0} spl · {zone.deviceCount ?? 0} ONUs
        </span>
        <ZoneActions zone={zone} ctx={ctx} />
      </div>
      {open &&
        subZones.map((sub) => <ZoneRow key={sub.id} zone={sub} ctx={ctx} depth={depth + 1} />)}
      {open &&
        zoneSplitters.map((splitter) => (
          <SplitterRow key={splitter.id} splitter={splitter} ctx={ctx} depth={depth + 1} />
        ))}
    </div>
  );
}

function SplitterRow({
  splitter,
  ctx,
  depth,
}: {
  splitter: Splitter;
  ctx: NodeContext;
  depth: number;
}) {
  const children = ctx.topology.splitters.filter((s) => s.parentId === splitter.id);
  const used = splitter.deviceCount ?? 0;
  const fillTone =
    used >= splitter.capacity ? 'danger' : used / splitter.capacity > 0.8 ? 'warn' : 'success';
  const feed = feedLabel(splitter, ctx.oltNames);

  return (
    <div>
      <div
        className="flex items-center gap-2 px-4 py-2 hover:bg-surface-2"
        style={{ paddingLeft: `${36 + depth * 20}px` }}
      >
        <NetworkIcon className="size-4 text-info" />
        <span className="min-w-0 flex-1 truncate text-sm text-ink">
          {splitter.name}
          <span className="ml-2 text-[10px] uppercase tracking-wide text-faint">
            {SPLITTER_KIND_LABELS[splitter.kind]} 1×{splitter.capacity}
          </span>
        </span>
        {feed && (
          <span className="flex items-center gap-1 text-[11px] text-faint" title="Fed from">
            <Cable className="size-3" />
            {feed}
          </span>
        )}
        {feedFibre(splitter) && (
          <span className="flex items-center gap-1 text-[11px] text-faint" title={feedFibre(splitter)!.title}>
            <Route className="size-3" />
            {feedFibre(splitter)!.text}
          </span>
        )}
        {splitter.notes && (
          <span title={splitter.notes} className="text-faint">
            <StickyNote className="size-3.5" />
          </span>
        )}
        {splitter.photoKey && (
          /*
            The housing's photograph (2026-09-16), taken from the phone's
            splitter sheet. The key changes on every re-take, so it rides the
            URL as a cache-buster; the session cookie carries the credential
            exactly as the field-job photographs' <img> does.
          */
          <a
            href={`/api/splitters/${encodeURIComponent(splitter.id)}/photo?v=${encodeURIComponent(splitter.photoKey)}`}
            target="_blank"
            rel="noreferrer"
            title={`Photograph of ${splitter.name} — open full size`}
            className="block shrink-0"
          >
            <img
              src={`/api/splitters/${encodeURIComponent(splitter.id)}/photo?v=${encodeURIComponent(splitter.photoKey)}`}
              alt={`Photograph of ${splitter.name}`}
              className="size-7 rounded border border-line object-cover"
            />
          </a>
        )}
        {splitter.lat === null && <Pill tone="warn">not placed</Pill>}
        <Pill state tone={fillTone}>
          {used}/{splitter.capacity}
        </Pill>
        <SplitterActions splitter={splitter} ctx={ctx} />
      </div>
      {(ctx.topology.opticalSplitters ?? [])
        .filter((device) => device.housingId === splitter.id)
        .map((device) => (
          <OpticalDeviceRow key={device.id} device={device} ctx={ctx} depth={depth + 1} />
        ))}
      {children.map((child) => (
        <SplitterRow key={child.id} splitter={child} ctx={ctx} depth={depth + 1} />
      ))}
    </div>
  );
}

/**
 * A physical device housed in the stage above it — the same registry the
 * Splitters page manages, shown in place so "what lives in this box" and
 * "what the light path does" read as one tree.
 */
function OpticalDeviceRow({
  device,
  ctx,
  depth,
}: {
  device: OpticalSplitter;
  ctx: NodeContext;
  depth: number;
}) {
  const { can } = useSession();
  const upstream = upstreamSummary(device, ctx.topology.opticalSplitters ?? []);
  return (
    <div
      className="flex items-center gap-2 px-4 py-1.5 hover:bg-surface-2"
      style={{ paddingLeft: `${36 + depth * 20}px` }}
    >
      <Split className="size-3.5 text-success" />
      <span className="min-w-0 flex-1 truncate text-sm text-ink">
        {device.name}
        <span className="ml-2 font-mono text-[10px] text-faint">1:{device.outputs}</span>
        {upstream && (
          <span className="ml-2 truncate text-[11px] text-faint" title={upstream}>
            {upstream}
          </span>
        )}
      </span>
      {can('topology.manage') && (
        <span className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            title="Edit splitter device"
            icon={<Pencil className="size-3.5" />}
            onClick={() => ctx.onEditDevice(device)}
          />
          <Button
            size="sm"
            variant="ghost"
            title="Delete splitter device"
            icon={<Trash2 className="size-3.5" />}
            onClick={() => ctx.onDeleteDevice(device)}
          />
        </span>
      )}
    </div>
  );
}

// --- node graph view ---------------------------------------------------------

/**
 * One node in the graph views.
 *
 * A tile for the icon, the name on its own line, then a row of small facts —
 * the same shape for a zone and a splitter, so a cascade reads as one diagram
 * rather than two kinds of box. Fixed width so a long name truncates instead
 * of stretching the node and shearing the connector lines that the CSS draws
 * against its edges.
 */
function GraphNode({
  icon,
  tone,
  title,
  kind,
  badge,
  facts,
  actions,
  fill,
}: {
  icon: ReactNode;
  tone: 'primary' | 'info';
  title: string;
  kind?: string;
  badge?: ReactNode;
  facts?: Array<{ icon?: ReactNode; text: string; title?: string; tone?: 'faint' | 'warn' }>;
  actions?: ReactNode;
  /** 0–1, drawn as a bar under the header. Splitters only. */
  fill?: { ratio: number; tone: 'success' | 'warn' | 'danger' };
}) {
  return (
    <div className="w-[19rem] max-w-full rounded-xl border border-line bg-surface shadow-sm">
      <div className="flex items-start gap-2.5 px-3 pt-2.5">
        <span
          className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
            tone === 'primary' ? 'bg-primary-soft text-primary' : 'bg-info-soft text-info'
          }`}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-ink" title={title}>
              {title}
            </span>
            {badge}
          </div>
          {kind && <span className="text-[10px] uppercase tracking-wider text-faint">{kind}</span>}
        </div>
      </div>

      {fill && (
        <div className="mt-2 px-3">
          <div className="h-1 overflow-hidden rounded-full bg-surface-2">
            <div
              className={`h-full rounded-full ${
                fill.tone === 'danger' ? 'bg-danger' : fill.tone === 'warn' ? 'bg-warn' : 'bg-success'
              }`}
              style={{ width: `${Math.min(100, Math.round(fill.ratio * 100))}%` }}
            />
          </div>
        </div>
      )}

      {facts && facts.length > 0 && (
        <div className="mt-1.5 space-y-0.5 px-3">
          {facts.map((fact) => (
            <div
              key={fact.text}
              title={fact.title ?? fact.text}
              className={`flex items-center gap-1 text-[11px] ${
                fact.tone === 'warn' ? 'text-warn' : 'text-faint'
              }`}
            >
              {fact.icon}
              <span className="truncate">{fact.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end px-2 pb-1.5 pt-1">{actions}</div>
    </div>
  );
}

function GraphZoneNode({ zone, ctx }: { zone: Zone; ctx: NodeContext }) {
  const subZones = ctx.topology.zones.filter((z) => z.parentId === zone.id);
  const zoneSplitters = ctx.topology.splitters.filter((s) => s.zoneId === zone.id && !s.parentId);
  const children = subZones.length + zoneSplitters.length;

  return (
    <li className="graph-node">
      <GraphNode
        icon={<MapPin className="size-4" />}
        tone="primary"
        title={zone.name}
        kind="Zone"
        badge={zone.lat === null ? <Pill tone="warn">not placed</Pill> : undefined}
        facts={[
          {
            text: `${zone.splitterCount ?? 0} splitter${(zone.splitterCount ?? 0) === 1 ? '' : 's'} · ${(zone.deviceCount ?? 0).toLocaleString()} ONUs`,
          },
        ]}
        actions={<ZoneActions zone={zone} ctx={ctx} />}
      />
      {children > 0 && (
        <ul className="graph-children">
          {subZones.map((sub) => (
            <GraphZoneNode key={sub.id} zone={sub} ctx={ctx} />
          ))}
          {zoneSplitters.map((splitter) => (
            <GraphSplitterNode key={splitter.id} splitter={splitter} ctx={ctx} />
          ))}
        </ul>
      )}
    </li>
  );
}

function GraphSplitterNode({ splitter, ctx }: { splitter: Splitter; ctx: NodeContext }) {
  const children = ctx.topology.splitters.filter((s) => s.parentId === splitter.id);
  const used = splitter.deviceCount ?? 0;
  const fillTone =
    used >= splitter.capacity ? 'danger' : used / splitter.capacity > 0.8 ? 'warn' : 'success';
  const feed = feedLabel(splitter, ctx.oltNames);
  /**
   * Splitter loss accumulated from the OLT down to and including this one —
   * shared math (`upstreamCapacities` + `cascadeLossDb`), the same the cascade
   * wizard's live readout uses. A cascade is what quietly kills a PON: each
   * stage is fine on its own, and the third one down is 27 dB from the OLT
   * before a metre of fibre is counted. This is a planning figure — typical
   * insertion loss per split, no fibre, splices or connectors — so it is
   * labelled as the splitters' share of the budget rather than as the budget.
   */
  const loss = cascadeLossDb(upstreamCapacities(splitter.id, ctx.topology.splitters));
  // The fibre measured down to here, when its routes are drawn (2026-09-16):
  // shown as its own fact, and counted into the amber judgement below — the
  // 28 dB line is one budget, and the cable spends from it too.
  const fibre = feedFibre(splitter);
  const fibreDb = splitter.feedLossDb ?? fibreLossDb(splitter.feedLengthM ?? null);

  const facts: Array<{ icon?: ReactNode; text: string; title?: string; tone?: 'faint' | 'warn' }> =
    [];
  if (feed) {
    facts.push({
      icon: <Cable className="size-3 shrink-0" />,
      text: feed,
      title: `Fed from ${feed}`,
    });
  }
  facts.push({
    icon: <Gauge className="size-3 shrink-0" />,
    text: `−${loss.toFixed(1)} dB of splitter loss from the OLT`,
    title:
      'Typical insertion loss of this splitter and every one above it. Fibre, splices and connectors are on top of this.',
    // Past the shared threshold there is no ordinary GPON class left to
    // spend: B+ has 28 dB of budget and C+ 32, before any fibre is counted.
    tone: loss + fibreDb > SPLITTER_LOSS_AMBER_DB ? 'warn' : 'faint',
  });
  if (fibre) {
    facts.push({
      icon: <Route className="size-3 shrink-0" />,
      text: fibre.text,
      title: fibre.title,
      tone: loss + fibreDb > SPLITTER_LOSS_AMBER_DB ? 'warn' : 'faint',
    });
  }
  if (splitter.notes) {
    facts.push({
      icon: <StickyNote className="size-3 shrink-0" />,
      text: splitter.notes,
      title: splitter.notes,
    });
  }

  return (
    <li className="graph-node">
      <GraphNode
        icon={<NetworkIcon className="size-4" />}
        tone="info"
        title={splitter.name}
        kind={`${SPLITTER_KIND_LABELS[splitter.kind]} · 1:${splitter.capacity}`}
        badge={
          <Pill state tone={fillTone}>
            {used}/{splitter.capacity}
          </Pill>
        }
        fill={{ ratio: splitter.capacity > 0 ? used / splitter.capacity : 0, tone: fillTone }}
        facts={facts}
        actions={<SplitterActions splitter={splitter} ctx={ctx} />}
      />
      {children.length > 0 && (
        <ul className="graph-children">
          {children.map((child) => (
            <GraphSplitterNode key={child.id} splitter={child} ctx={ctx} />
          ))}
        </ul>
      )}
    </li>
  );
}

// --- map ---------------------------------------------------------------------

const dot = (color: string, size = 14) =>
  L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid rgba(255,255,255,.85);box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

/** The start pin: a proper teardrop so it reads as "here", not as a marker. */
const startPinIcon = () =>
  L.divIcon({
    className: '',
    html: '<svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg"><path d="M13 1C6.4 1 1 6.4 1 13c0 8.8 12 20 12 20s12-11.2 12-20C25 6.4 19.6 1 13 1z" fill="#e11d48" stroke="#fff" stroke-width="1.5"/><circle cx="13" cy="13" r="4.5" fill="#fff"/></svg>',
    iconSize: [26, 34],
    iconAnchor: [13, 34],
  });

/**
 * The corner handles of "Edit corners": a square for a corner that exists,
 * a smaller dot on each edge for the corner a drag would add. Both are
 * `L.marker`s and not `circleMarker`s because only a marker is draggable.
 */
const cornerHandleIcon = (color: string) =>
  L.divIcon({
    className: '',
    html: `<div style="width:12px;height:12px;border-radius:2px;background:#fff;border:2px solid ${color};box-shadow:0 1px 3px rgba(0,0,0,.45);cursor:grab"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });

const midpointHandleIcon = (color: string) =>
  L.divIcon({
    className: '',
    html: `<div style="width:8px;height:8px;border-radius:50%;background:${color};opacity:.7;border:1.5px solid #fff;cursor:copy"></div>`,
    iconSize: [8, 8],
    iconAnchor: [4, 4],
  });

interface GeocodeResult {
  label: string;
  lat: number;
  lng: number;
}

/** One colour per zone, by its place in the list — the layer and the draw preview agree. */
const ZONE_PALETTE = ['#6366f1', '#0ea5e9', '#22c55e', '#f59e0b', '#ec4899', '#14b8a6'];

/**
 * Fibre routes (2026-09-16): orange, which no zone in the palette above uses
 * and which reads as "cable" against both basemaps. A click on a route ends
 * on a splitter marker when it lands within this many metres of one — a
 * marker is a 12-px dot, and at street zoom 30 m is about the width of the
 * dot plus the shake of a hand.
 */
const FIBRE_COLOUR = '#f97316';
const ROUTE_SNAP_M = 30;

/** A splitter as the route drawer snaps to it. */
type RouteMarker = { id: string; name: string; at: LatLng };
/** Where a route being drawn starts: a snapped splitter, or `olt` when the first click hit no marker. */
type RouteStart = RouteMarker | 'olt' | null;

/**
 * How a drawn ring settles (2026-09-15). `push`: the ring is stored as drawn
 * and the neighbours move — pushed off the ground it covers, pulled onto the
 * ground a redraw released beside them. `clip`: the older rule, the drawn
 * ring yields to its neighbours. `cut`: the ring is taken OUT of the zone
 * being drawn, as a hole or a notch, and becomes empty space or a new zone.
 * `edit` (2026-09-16, operator: make topology easier): the SAVED ring is put
 * on the map with a handle on every corner and on every edge — drag a
 * corner, drag a midpoint to add one, right-click a corner to remove it —
 * and settles by the `push` rule, so a nudged corner moves the neighbours
 * exactly as a redraw would. `split` (2026-09-16, operator: draw a path
 * across a zone and both halves become zones): the clicks lay a LINE, not a
 * ring; the shared `splitZone` extends it across the zone and the zone keeps
 * one half while a new sibling zone is made of the other.
 */
type DrawMode = 'push' | 'clip' | 'cut' | 'edit' | 'split';

/** The two tones the halves of a split are painted in — neither is the zone's own, because both halves are "this zone" until Finish. */
const SPLIT_TONES = ['#6366f1', '#14b8a6'] as const;

/** How many corner edits Undo can walk back — enough for a session of nudging, bounded so a long one cannot pile up rings. */
const EDIT_HISTORY = 50;

/** What the map shows while drawing, whatever the mode: the same shape the API will store. */
type DrawPreview = {
  boundary: [number, number][] | null;
  holes: [number, number][][];
  neighbours: NeighbourMove[];
  removed: ZoneShape | null;
  /** The two halves of a split, larger first — painted in `SPLIT_TONES`. */
  split: [ZoneShape, ZoneShape] | null;
  notice: string | null;
  error: string | null;
};

function TopologyMap({
  topology,
  olts,
  heatOn,
  drawingZoneId,
  onDrawExit,
  drawingRoute,
  onRouteExit,
  canManage,
}: {
  topology: Topology;
  olts: Olt[];
  heatOn: boolean;
  drawingZoneId: string | null;
  onDrawExit: () => void;
  drawingRoute: boolean;
  onRouteExit: () => void;
  canManage: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);
  const heatRef = useRef<L.Layer | null>(null);
  const boundariesRef = useRef<L.LayerGroup | null>(null);
  const routesRef = useRef<L.LayerGroup | null>(null);
  /** The topology the map was last fitted to — fit on new data, never on a mode change. */
  const fittedRef = useRef<Topology | null>(null);
  const tasks = useTasks();
  const layersControlRef = useRef<L.Control.Layers | null>(null);
  const streetsRef = useRef<L.TileLayer | null>(null);
  const googleLayersRef = useRef<L.TileLayer[]>([]);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, { center: [-1.2385, 36.79], zoom: 12 });
    const streets = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);
    const satellite = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 19, attribution: '© Esri' },
    );
    // These two need no key and are always here. Google, when an operator has
    // configured it, is ADDED to this control — never in place of them.
    streetsRef.current = streets;
    layersControlRef.current = L.control
      .layers({ Streets: streets, Satellite: satellite })
      .addTo(map);

    mapRef.current = map;
    boundariesRef.current = L.layerGroup().addTo(map);
    // Routes sit above the boundaries and under the markers, like the cable does.
    routesRef.current = L.layerGroup().addTo(map);
    // Zoomed out, nearby zones and splitters collapse into a count bubble
    // instead of a smear of dots.
    overlayRef.current = L.markerClusterGroup({
      maxClusterRadius: 60,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) =>
        L.divIcon({
          className: '',
          html: `<div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:50%;background:#6366f1;color:#fff;font:600 12px system-ui;border:2px solid rgba(255,255,255,.85);box-shadow:0 1px 4px rgba(0,0,0,.5)">${cluster.getChildCount()}</div>`,
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        }),
    }).addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  /**
   * Google basemaps, when an operator has configured them.
   *
   * The tile URL points at THIS server, not at Google: the Map Tiles API wants
   * the key in the tile URL, so the API proxies the tiles and the key never
   * reaches the browser. When Google is not configured — the default — this
   * adds nothing and the layer picker is exactly what it always was.
   */
  const basemapsQuery = useQuery({
    queryKey: ['map-provider', 'basemaps'],
    queryFn: () =>
      api.get<{
        google: {
          available: boolean;
          mapTypes: string[];
          urlTemplate: string;
          attribution: string;
          maxZoom: number;
        };
      }>('/map-provider/basemaps'),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const map = mapRef.current;
    const control = layersControlRef.current;
    if (!map || !control) return;

    // Switched off since last time: take the layers back out, and if one of
    // them was on screen, fall back to streets rather than to a blank map.
    let wasShowing = false;
    for (const layer of googleLayersRef.current) {
      if (map.hasLayer(layer)) {
        wasShowing = true;
        map.removeLayer(layer);
      }
      control.removeLayer(layer);
    }
    googleLayersRef.current = [];
    if (wasShowing && streetsRef.current) streetsRef.current.addTo(map);

    const google = basemapsQuery.data?.google;
    if (!google?.available) return;

    const LABELS: Record<string, string> = {
      roadmap: 'Google streets',
      satellite: 'Google satellite',
      terrain: 'Google terrain',
    };
    for (const mapType of google.mapTypes) {
      const layer = L.tileLayer(google.urlTemplate.replace('{mapType}', mapType), {
        maxZoom: google.maxZoom,
        attribution: google.attribution,
      });
      control.addBaseLayer(layer, LABELS[mapType] ?? `Google ${mapType}`);
      googleLayersRef.current.push(layer);
    }
  }, [basemapsQuery.data]);

  useEffect(() => {
    const map = mapRef.current;
    const overlay = overlayRef.current;
    if (!map || !overlay) return;

    overlay.clearLayers();

    const bounds: Array<[number, number]> = [];
    /*
      Markers listen for clicks to open their popups, and Leaflet hands a click
      to the topmost layer that listens — so while a route is being laid, a
      click ON a splitter marker (exactly where the panel says to click) never
      reached the map and never snapped. Non-interactive while anything is
      drawn, as the boundaries already are; the snap finds the marker by
      distance, not by the marker hearing the click.
    */
    const busy = Boolean(drawingZoneId) || drawingRoute;

    for (const zone of topology.zones) {
      if (zone.lat === null || zone.lng === null) continue;
      bounds.push([zone.lat, zone.lng]);
      L.marker([zone.lat, zone.lng], { icon: dot('#6366f1', 16), interactive: !busy })
        .bindPopup(
          `<b>${zone.name}</b><br>${zone.splitterCount ?? 0} splitters · ${zone.deviceCount ?? 0} ONUs`,
        )
        .addTo(overlay);
    }
    for (const splitter of topology.splitters) {
      if (splitter.lat === null || splitter.lng === null) continue;
      bounds.push([splitter.lat, splitter.lng]);
      L.marker([splitter.lat, splitter.lng], { icon: dot('#38bdf8', 12), interactive: !busy })
        .bindPopup(
          `<b>${splitter.name}</b><br>${splitter.deviceCount ?? 0}/${splitter.capacity} ports used`,
        )
        .addTo(overlay);
    }

    if (heatRef.current) {
      map.removeLayer(heatRef.current);
      heatRef.current = null;
    }
    if (heatOn && topology.heat.length > 0) {
      const max = Math.max(...topology.heat.map((p) => p.weight));
      heatRef.current = L.heatLayer(
        topology.heat.map((p) => [p.lat, p.lng, p.weight / max]),
        { radius: 35, blur: 25, maxZoom: 17 },
      ).addTo(map);
    }

    // Fit once per topology, not on every rebuild: this effect now also reruns
    // when drawing starts or stops, and a fit then would yank the map out from
    // under the operator between their first click and their second.
    if (bounds.length > 0 && fittedRef.current !== topology) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      fittedRef.current = topology;
    }
  }, [topology, heatOn, drawingZoneId, drawingRoute]);

  /**
   * Existing zone boundaries: quiet translucent shapes under the markers, each
   * named on hover. Redrawn whenever the zones change, so a saved boundary
   * appears without a reload.
   *
   * Its own effect, and NOT part of the overlay one, because it also depends on
   * whether a draw is in progress — and the overlay effect ends in fitBounds,
   * which would yank the map out from under the operator the moment they
   * started drawing.
   *
   * `interactive: false` while drawing is the point. Leaflet gives a click to
   * the topmost layer that LISTENS for one, and a tooltipped polygon listens,
   * so a corner laid on top of a saved boundary never reached the map at all —
   * silently dropped, the panel's point count simply not going up. That was
   * survivable when an overlapping boundary was refused anyway. It is fatal
   * now: drawing ACROSS a neighbour and letting the clip settle it is the
   * whole feature, and it cannot be done if the neighbour swallows the clicks.
   */
  useEffect(() => {
    const boundaries = boundariesRef.current;
    if (!boundaries) return;
    boundaries.clearLayers();
    topology.zones.forEach((zone, i) => {
      if (!zone.boundary || zone.boundary.length < 3) return;
      // Holes are Leaflet's own second-and-later rings: a cut-out reads as a gap.
      const shape = L.polygon([zone.boundary, ...(zone.holes ?? [])] as [number, number][][], {
        color: ZONE_PALETTE[i % ZONE_PALETTE.length]!,
        weight: 2,
        fillOpacity: 0.07,
        interactive: !drawingZoneId,
      });
      // "Riverside · 12 ONUs": the chassis ONU rows placed in the zone (2026-09-16).
      if (!drawingZoneId) shape.bindTooltip(`${zone.name} · ${(zone.onuCount ?? 0).toLocaleString()} ONU${(zone.onuCount ?? 0) === 1 ? '' : 's'}`, { sticky: true });
      shape.addTo(boundaries);
    });
  }, [topology.zones, drawingZoneId]);

  /**
   * The fibre routes (2026-09-16): each an orange polyline with its measured
   * length as a permanent label, and a popup naming both ends, the length
   * and the dB it costs, with Delete for an operator who may. Like the
   * boundaries, `interactive: false` while anything is being drawn, so a
   * bend laid across a route reaches the map.
   */
  const [routeDeleting, setRouteDeleting] = useState<FibreRoute | null>(null);
  const splitterName = useMemo(() => new Map(topology.splitters.map((s) => [s.id, s.name] as const)), [topology.splitters]);
  const oltName = useMemo(() => new Map(olts.map((o) => [o.id, o.name] as const)), [olts]);
  const routeEndName = (route: FibreRoute, end: 'from' | 'to'): string =>
    end === 'to'
      ? (splitterName.get(route.toSplitterId) ?? 'a removed splitter')
      : route.fromKind === 'olt'
        ? (oltName.get(route.fromId) ?? 'the OLT')
        : (splitterName.get(route.fromId) ?? 'a removed splitter');

  useEffect(() => {
    const layer = routesRef.current;
    if (!layer) return;
    layer.clearLayers();
    const busy = Boolean(drawingZoneId) || drawingRoute;
    for (const route of topology.fibreRoutes ?? []) {
      if (route.path.length < 2) continue;
      const line = L.polyline(route.path, { color: FIBRE_COLOUR, weight: 3, opacity: 0.85, interactive: !busy });
      line.bindTooltip(formatDistance(route.lengthM), { permanent: true, direction: 'center', className: 'fibre-length' });
      if (!busy) {
        // Built as DOM, not markup: names are the operator's own text.
        const el = document.createElement('div');
        const title = document.createElement('b');
        title.textContent = `${routeEndName(route, 'from')} → ${routeEndName(route, 'to')}`;
        el.appendChild(title);
        const figures = document.createElement('div');
        figures.textContent = `${formatDistance(route.lengthM)} · +${fibreLossDb(route.lengthM).toFixed(1)} dB at ${FIBRE_DB_PER_KM} dB/km`;
        el.appendChild(figures);
        if (route.note) {
          const note = document.createElement('div');
          note.style.opacity = '0.7';
          note.textContent = route.note;
          el.appendChild(note);
        }
        if (canManage) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = 'Delete route';
          button.style.cssText = 'margin-top:6px;font:600 11px system-ui;color:#dc2626;background:none;border:0;padding:0;cursor:pointer;text-decoration:underline';
          button.onclick = () => {
            line.closePopup();
            setRouteDeleting(route);
          };
          el.appendChild(button);
        }
        line.bindPopup(el);
      }
      line.addTo(layer);
    }
  }, [topology.fibreRoutes, topology.splitters, olts, drawingZoneId, drawingRoute, canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Finding WHERE the zone is: search a place name, or drop a pin by hand.
   * Either way the pin marks the start point — when drawing begins it is
   * consumed as corner 1, so the operator lands exactly where they aimed.
   */
  const [q, setQ] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchNote, setSearchNote] = useState<string | null>(null);
  const [pin, setPin] = useState<[number, number] | null>(null);
  const [pinArmed, setPinArmed] = useState(false);
  const pinMarkerRef = useRef<L.Marker | null>(null);
  const pinNow = useRef<[number, number] | null>(null);
  pinNow.current = pin;

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setListOpen(false);
      setSearchNote(null);
      setSearching(false);
      return;
    }
    let stale = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.get<{
          results: GeocodeResult[];
          provider?: string;
          /**
           * Set when the configured provider could not answer — no key, a
           * refused key, an exhausted quota — and these results came from the
           * fallback instead. Shown rather than swallowed: results that are
           * quietly not the ones you configured are worse than a sentence.
           */
          notice?: string | null;
        }>(`/geocode?q=${encodeURIComponent(trimmed)}`);
        if (stale) return;
        setResults(r.results);
        setSearchNote(
          r.notice ?? (r.results.length === 0 ? 'Nothing found by that name.' : null),
        );
        setListOpen(true);
      } catch {
        if (stale) return;
        setResults([]);
        setSearchNote('The map search did not answer — try again in a moment.');
        setListOpen(true);
      } finally {
        if (!stale) setSearching(false);
      }
    }, 400);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [q]);

  const chooseResult = (r: GeocodeResult) => {
    setListOpen(false);
    setQ(r.label.split(',')[0] ?? r.label);
    const map = mapRef.current;
    if (map) map.flyTo([r.lat, r.lng], Math.max(map.getZoom(), 15));
    // The found place becomes the start pin — searching IS choosing a start.
    if (canManage && !drawingZoneId) setPin([r.lat, r.lng]);
  };

  // The pin itself: draggable, so a search hit can be nudged onto the corner.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pin) {
      pinMarkerRef.current?.remove();
      pinMarkerRef.current = null;
      return;
    }
    if (pinMarkerRef.current) {
      pinMarkerRef.current.setLatLng(pin);
      return;
    }
    pinMarkerRef.current = L.marker(pin, { icon: startPinIcon(), draggable: true, zIndexOffset: 900 })
      .bindTooltip('Zone start point — drag to adjust', { direction: 'top', offset: [0, -32] })
      .on('dragend', () => {
        const at = pinMarkerRef.current!.getLatLng();
        setPin([at.lat, at.lng]);
      })
      .addTo(map);
  }, [pin]);

  // Armed pin mode: the next map click places the pin instead of anything else.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pinArmed) return;
    const container = map.getContainer();
    container.style.cursor = 'crosshair';
    const onClick = (e: L.LeafletMouseEvent) => {
      setPin([e.latlng.lat, e.latlng.lng]);
      setPinArmed(false);
    };
    map.on('click', onClick);
    return () => {
      map.off('click', onClick);
      container.style.cursor = '';
    };
  }, [pinArmed]);

  // Drawing and pin-dropping are the same click — never both.
  useEffect(() => {
    if (drawingZoneId || drawingRoute) setPinArmed(false);
  }, [drawingZoneId, drawingRoute]);

  /**
   * Laying a fibre route (2026-09-16, operator: fibre routes as lines between
   * splitters with measured length). The same click-to-lay-points mechanics
   * as a boundary, with two differences: a click within ROUTE_SNAP_M of a
   * splitter marker lands ON the marker (so the line's end is the splitter's
   * own position, not a hand's width off it), and the first and last points
   * are what the route is FROM and TO. A first click that hits no marker
   * starts the route at the OLT — the chassis has no position on the map, so
   * that hop begins wherever the operator says the fibre leaves the building
   * and the panel asks which OLT. The live length is `pathLengthMetres` over
   * the points laid, the same function the API measures the stored path with.
   */
  const [routePoints, setRoutePoints] = useState<LatLng[]>([]);
  const [routeSnaps, setRouteSnaps] = useState<Array<RouteMarker | null>>([]);
  const [routeOltId, setRouteOltId] = useState('');
  const [routeNote, setRouteNote] = useState('');
  const [routeNear, setRouteNear] = useState<string | null>(null);
  const routeLayerRef = useRef<L.LayerGroup | null>(null);
  const routePointsRef = useRef<LatLng[]>([]);
  const routeSnapsRef = useRef<Array<RouteMarker | null>>([]);
  const routeRedrawRef = useRef<(cursor?: LatLng) => void>(() => {});
  const routeMarkers = useMemo<RouteMarker[]>(
    () =>
      topology.splitters
        .filter((s) => s.lat !== null && s.lng !== null)
        .map((s) => ({ id: s.id, name: s.name, at: [s.lat!, s.lng!] as LatLng })),
    [topology.splitters],
  );
  const routeMarkersRef = useRef(routeMarkers);
  routeMarkersRef.current = routeMarkers;
  const nearestMarker = (p: LatLng): RouteMarker | null => {
    let best: RouteMarker | null = null;
    let bestD = ROUTE_SNAP_M;
    for (const m of routeMarkersRef.current) {
      const d = distanceMetres(p, m.at);
      if (d <= bestD) {
        best = m;
        bestD = d;
      }
    }
    return best;
  };
  /** The two ends, read off the snaps: point 1 is FROM (a marker, or the OLT), the last point is TO when it is a marker other than FROM. */
  const routeEndsOf = (snaps: Array<RouteMarker | null>): { from: RouteStart; to: RouteMarker | null } => {
    if (snaps.length === 0) return { from: null, to: null };
    const from: RouteStart = snaps[0] ?? 'olt';
    const last = snaps.length >= 2 ? snaps[snaps.length - 1] : null;
    const to = last && (from === 'olt' || last.id !== from.id) ? last : null;
    return { from, to };
  };
  const routeEnds = routeEndsOf(routeSnaps);
  const routeLength = pathLengthMetres(routePoints);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!drawingRoute) {
      routeLayerRef.current?.remove();
      routeLayerRef.current = null;
      routePointsRef.current = [];
      routeSnapsRef.current = [];
      setRoutePoints([]);
      setRouteSnaps([]);
      setRouteNear(null);
      map.getContainer().style.cursor = '';
      return;
    }
    const layer = L.layerGroup().addTo(map);
    routeLayerRef.current = layer;
    map.getContainer().style.cursor = 'crosshair';
    setSavedNotice(null);
    setRouteNote('');
    setRouteOltId('');

    const redraw = (cursor?: LatLng) => {
      layer.clearLayers();
      const points = routePointsRef.current;
      const snaps = routeSnapsRef.current;
      if (points.length >= 2) L.polyline(points, { color: FIBRE_COLOUR, weight: 3 }).addTo(layer);
      // The segment the next click would add, dashed, from the last point to the cursor.
      if (cursor && points.length >= 1) {
        L.polyline([points[points.length - 1]!, cursor], { color: FIBRE_COLOUR, weight: 2, dashArray: '6 4', opacity: 0.7 }).addTo(layer);
      }
      points.forEach((p, i) => {
        const snapped = snaps[i] !== null && snaps[i] !== undefined;
        L.circleMarker(p, {
          radius: snapped ? 6 : 4,
          color: FIBRE_COLOUR,
          fillColor: snapped ? '#fff' : FIBRE_COLOUR,
          fillOpacity: 1,
          weight: 2,
        }).addTo(layer);
      });
      // A ring around the marker the cursor would snap to, so the operator
      // sees the landing before the click.
      const near = cursor ? nearestMarker(cursor) : null;
      if (near) L.circleMarker(near.at, { radius: 11, color: FIBRE_COLOUR, fill: false, weight: 2, dashArray: '3 3' }).addTo(layer);
      setRouteNear(near?.name ?? null);
    };
    routeRedrawRef.current = redraw;

    const onClick = (e: L.LeafletMouseEvent) => {
      const clicked: LatLng = [e.latlng.lat, e.latlng.lng];
      const snaps = routeSnapsRef.current;
      const { from } = routeEndsOf(snaps);
      const hit = nearestMarker(clicked);
      // The first click may snap to any marker; a later one to any marker but
      // the start — a click back on the start is a bend laid by hand, not an end.
      const snap = hit && (snaps.length === 0 || from === 'olt' || from === null || hit.id !== from.id) ? hit : null;
      routePointsRef.current = [...routePointsRef.current, snap ? snap.at : clicked];
      routeSnapsRef.current = [...snaps, snap];
      setRoutePoints(routePointsRef.current);
      setRouteSnaps(routeSnapsRef.current);
      redraw();
    };
    const onMove = (e: L.LeafletMouseEvent) => redraw([e.latlng.lat, e.latlng.lng]);
    map.on('click', onClick);
    map.on('mousemove', onMove);
    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMove);
      layer.remove();
      map.getContainer().style.cursor = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingRoute]);

  const undoRoutePoint = () => {
    routePointsRef.current = routePointsRef.current.slice(0, -1);
    routeSnapsRef.current = routeSnapsRef.current.slice(0, -1);
    setRoutePoints(routePointsRef.current);
    setRouteSnaps(routeSnapsRef.current);
    routeRedrawRef.current();
  };

  const canFinishRoute =
    routeEnds.from !== null &&
    routeEnds.to !== null &&
    routePoints.length >= 2 &&
    (routeEnds.from !== 'olt' || routeOltId !== '');

  /**
   * Finish leaves the map at once and hands the write to the tasks tray, as
   * every write dialog does; the tray's done line carries the length the API
   * measured — the number that was stored, not the one the panel showed.
   */
  const finishRoute = () => {
    const { from, to } = routeEnds;
    if (!from || !to) return;
    const fromName = from === 'olt' ? (oltName.get(routeOltId) ?? 'the OLT') : from.name;
    const body = {
      fromKind: from === 'olt' ? 'olt' : 'splitter',
      fromId: from === 'olt' ? routeOltId : from.id,
      toSplitterId: to.id,
      path: routePointsRef.current,
      note: routeNote.trim() || null,
    };
    const label = `Fibre route ${fromName} → ${to.name}`;
    onRouteExit();
    tasks.start<FibreRoute>({
      label,
      run: () => api.post<FibreRoute>('/fibre-routes', body),
      invalidate: [['topology']],
      done: (r) => `${label}: ${formatDistance(r.lengthM)} · +${fibreLossDb(r.lengthM).toFixed(1)} dB.`,
    });
  };

  /**
   * Draw mode. Clicks lay vertices; the preview redraws on every click AND
   * every cursor move, clipped live by the same `clipBoundary` the server saves
   * with. An overlapping shape is no longer refused — it is pushed off the
   * neighbour's edge and the operator watches that happen, so the shape they
   * press Finish on is the shape that gets stored. Only a shape with nothing
   * left of it turns red.
   */
  const [drawPoints, setDrawPoints] = useState<[number, number][]>([]);
  const [preview, setPreview] = useState<DrawPreview | null>(null);
  const [seededFromPin, setSeededFromPin] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState<DrawMode>('push');
  const [cutInto, setCutInto] = useState<'empty' | 'zone'>('empty');
  const [cutName, setCutName] = useState('');
  // Split: the new sibling's name, and (optionally) a new name for the half this zone keeps.
  const [splitName, setSplitName] = useState('');
  const [splitKeepName, setSplitKeepName] = useState('');
  const drawLayerRef = useRef<L.LayerGroup | null>(null);
  const pointsRef = useRef<[number, number][]>([]);
  const redrawRef = useRef<(cursor?: [number, number]) => void>(() => {});
  /**
   * Edit corners: the rings before each edit (Undo walks them back), the
   * corners DRAWN before the operator switched to editing (handed back when
   * they switch away), and a count the Undo button can render.
   */
  const historyRef = useRef<[number, number][][]>([]);
  const parkedRef = useRef<[number, number][]>([]);
  const [editHistoryLen, setEditHistoryLen] = useState(0);
  const drawingZone = topology.zones.find((z) => z.id === drawingZoneId) ?? null;
  const colourById = new Map(topology.zones.map((z, i) => [z.id, ZONE_PALETTE[i % ZONE_PALETTE.length]!] as const));

  /**
   * The judge, by mode — every one of them the shared function the API saves
   * with, so the preview is the stored shape. Held in a ref so the draw
   * effect (keyed on the zone) sees a mode change on the next cursor move.
   */
  const judgeCandidate = (candidate: [number, number][]): DrawPreview => {
    const rows = topology.zones.map((z) => ({
      id: z.id,
      parentId: z.parentId,
      name: z.name,
      boundary: (z.boundary ?? null) as LatLng[] | null,
      holes: (z.holes ?? null) as LatLng[][] | null,
    }));
    const previous = drawingZone
      ? shapeOf({ boundary: (drawingZone.boundary ?? null) as LatLng[] | null, holes: (drawingZone.holes ?? null) as LatLng[][] | null })
      : null;
    if (drawMode === 'cut') {
      if (!previous) {
        return { boundary: null, holes: [], neighbours: [], removed: null, split: null, notice: null, error: 'This zone has no boundary to cut out of — draw one first.' };
      }
      const cut = cutOut(candidate, previous, drawingZone?.name ?? 'this zone');
      return {
        boundary: cut.remaining?.boundary ?? null,
        holes: cut.remaining?.holes ?? [],
        neighbours: [],
        removed: cut.removed,
        split: null,
        notice: cut.notice,
        error: cut.error,
      };
    }
    if (drawMode === 'split') {
      if (!previous) {
        return { boundary: null, holes: [], neighbours: [], removed: null, split: null, notice: null, error: 'This zone has no boundary to split — draw one first.' };
      }
      // The candidate is the LINE here. `boundary` carries the larger half so
      // the red/finishable rule below reads the same for every mode.
      const s = splitZone(candidate, previous, drawingZone?.name ?? 'this zone');
      return {
        boundary: s.pieces?.[0].boundary ?? null,
        holes: s.pieces?.[0].holes ?? [],
        neighbours: [],
        removed: null,
        split: s.pieces,
        notice: s.notice,
        error: s.error,
      };
    }
    if (drawMode === 'clip') {
      const clip = clipBoundary(candidate, drawingZoneId, drawingZone?.parentId ?? null, rows);
      return { boundary: clip.boundary, holes: clip.holes, neighbours: [], removed: null, split: null, notice: clip.notice, error: clip.error };
    }
    const r = redrawWithNeighbours(previous, candidate, drawingZoneId, drawingZone?.parentId ?? null, rows);
    return { boundary: r.boundary, holes: r.holes, neighbours: r.neighbours, removed: null, split: null, notice: r.notice, error: r.error };
  };
  const judgeRef = useRef(judgeCandidate);
  judgeRef.current = judgeCandidate;
  const colourRef = useRef(colourById);
  colourRef.current = colourById;
  // The draw effect's click and move handlers ask this, not the state: a
  // handle drag must never see a stale mode and lay a corner under itself.
  const modeRef = useRef<DrawMode>(drawMode);
  modeRef.current = drawMode;

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!drawingZoneId) {
      drawLayerRef.current?.remove();
      drawLayerRef.current = null;
      setDrawPoints([]);
      setPreview(null);
      setSeededFromPin(false);
      map.getContainer().style.cursor = '';
      return;
    }

    const layer = L.layerGroup().addTo(map);
    drawLayerRef.current = layer;
    map.getContainer().style.cursor = 'crosshair';
    setSavedNotice(null); // the last save's sentence is not about this shape
    setCutInto('empty');
    setCutName('');
    setSplitName('');
    setSplitKeepName('');
    // A zone with nothing drawn yet has nothing to cut out of or split; and an
    // edit is of THIS zone's saved ring, loaded when the operator asks for it.
    setDrawMode((m) => (m === 'cut' || m === 'edit' || m === 'split' ? 'push' : m));
    if (modeRef.current === 'cut' || modeRef.current === 'edit' || modeRef.current === 'split') modeRef.current = 'push';
    historyRef.current = [];
    parkedRef.current = [];
    setEditHistoryLen(0);
    // A dropped pin is the promised start point: consume it as corner 1.
    const seed = pinNow.current;
    pointsRef.current = seed ? [seed] : [];
    setDrawPoints(pointsRef.current);
    setSeededFromPin(seed !== null);
    if (seed) setPin(null);

    /**
     * One corner edit landed (a drag ended, a midpoint became a corner, a
     * corner was removed): the ring before it goes on the Undo stack and the
     * map re-judges the new one through the same judge a drawn ring gets. A
     * helper that REFUSED hands the same array back — nothing changed, so
     * nothing is stacked and nothing is redrawn.
     */
    const applyEdit = (next: [number, number][]) => {
      if (next === pointsRef.current) return;
      historyRef.current = [...historyRef.current.slice(-(EDIT_HISTORY - 1)), pointsRef.current];
      setEditHistoryLen(historyRef.current.length);
      pointsRef.current = next;
      setDrawPoints(next);
      redrawRef.current();
    };

    /**
     * The handles of "Edit corners", rebuilt on every redraw. While one is
     * dragged the two outlines follow it un-judged — the judge runs once, at
     * drag end, because running polygon-clipping on every mouse move is what
     * would make a drag stutter — and a drag that ends where it began still
     * re-judges, which is cheap and never wrong. Each handle closes over the
     * ring it was built for, and the helpers refuse a stale index, so a
     * handle outliving its ring by a tick cannot corrupt the next one.
     */
    const addHandles = (follow: { drawn: L.Polygon | null; stored: L.Polygon | null }, holes: [number, number][][], color: string) => {
      const ring = pointsRef.current;
      const live = (next: [number, number][]) => {
        follow.drawn?.setLatLngs(next);
        follow.stored?.setLatLngs([next, ...holes]);
      };
      const at = (m: L.Marker): [number, number] => {
        const p = m.getLatLng();
        return [p.lat, p.lng];
      };
      ring.forEach((p, i) => {
        const handle = L.marker(p, {
          icon: cornerHandleIcon(color),
          draggable: true,
          zIndexOffset: 1000,
          title:
            ring.length > MIN_RING_CORNERS
              ? 'Drag to move this corner · right-click to remove it'
              : 'Drag to move this corner · a boundary keeps at least three',
        });
        handle.on('drag', () => live(moveVertex(ring, i, at(handle))));
        handle.on('dragend', () => applyEdit(moveVertex(ring, i, at(handle))));
        handle.on('contextmenu', (e: L.LeafletMouseEvent) => {
          // Leaflet already keeps the browser's menu off a right-clicked
          // layer; stopping it here also keeps the map from hearing it.
          L.DomEvent.stop(e.originalEvent);
          applyEdit(deleteVertex(ring, i));
        });
        handle.addTo(layer);
      });
      midpoints(ring).forEach((p, i) => {
        const handle = L.marker(p, {
          icon: midpointHandleIcon(color),
          draggable: true,
          zIndexOffset: 900,
          title: 'Drag to add a corner here',
        });
        handle.on('drag', () => live(insertVertex(ring, i, at(handle))));
        handle.on('dragend', () => applyEdit(insertVertex(ring, i, at(handle))));
        handle.addTo(layer);
      });
    };

    const redraw = (cursor?: [number, number]) => {
      layer.clearLayers();
      const editing = modeRef.current === 'edit';
      const splitting = modeRef.current === 'split';
      const points = pointsRef.current;
      // Editing lays no corner by clicking, so the cursor is not a corner-to-be.
      const candidate = cursor && !editing ? [...points, cursor] : points;
      // A line is a line from two points; a ring needs three.
      const clip = candidate.length >= (splitting ? 2 : 3) ? judgeRef.current(candidate) : null;
      setPreview(clip);
      // Red is reserved for a shape that cannot be saved at all — nothing left
      // of it, or a ring that crosses itself. A neighbour moving is normal.
      const bad = clip !== null && clip.boundary === null;
      const color = bad ? '#dc2626' : '#6366f1';
      // The two outlines of this zone, kept so a handle drag can move them live.
      const follow: { drawn: L.Polygon | null; stored: L.Polygon | null } = { drawn: null, stored: null };
      if (splitting) {
        // The line as drawn, and the two halves it makes — each a solid fill
        // in its own tone, holes as gaps — so both zones-to-be are visible
        // before Finish. The line is never closed into a ring on screen.
        if (candidate.length >= 2) L.polyline(candidate, { color, weight: 2, dashArray: '6 4' }).addTo(layer);
        clip?.split?.forEach((half, i) => {
          L.polygon([half.boundary, ...half.holes] as [number, number][][], {
            color: SPLIT_TONES[i]!,
            weight: 2,
            fillOpacity: 0.2,
          }).addTo(layer);
        });
      } else if (candidate.length >= 3) {
        // The dashed outline is what the cursor drew; the solid fill is what
        // will be stored for THIS zone (holes drawn as gaps); each neighbour
        // that moves is redrawn in its own colour, dashed, where it will end
        // up — so a push, a pull and a cut are all visible before Finish.
        follow.drawn = L.polygon(candidate, {
          color,
          weight: 1,
          opacity: 0.55,
          dashArray: '6 4',
          fill: false,
        }).addTo(layer);
        if (clip?.boundary) {
          follow.stored = L.polygon([clip.boundary, ...clip.holes] as [number, number][][], { color, weight: 2, fillOpacity: 0.12 }).addTo(layer);
        }
        if (clip?.removed) {
          L.polygon([clip.removed.boundary, ...clip.removed.holes] as [number, number][][], {
            color: '#dc2626',
            weight: 1,
            fillOpacity: 0.25,
            dashArray: '2 3',
          }).addTo(layer);
        }
        for (const n of clip?.neighbours ?? []) {
          L.polygon([n.boundary, ...n.holes] as [number, number][][], {
            color: colourRef.current.get(n.id) ?? '#64748b',
            weight: 2,
            dashArray: '4 3',
            fillOpacity: 0.05,
          }).addTo(layer);
        }
      } else if (candidate.length === 2) {
        L.polyline(candidate, { color, weight: 2, dashArray: '6 4' }).addTo(layer);
      }
      if (editing) {
        addHandles(follow, clip?.holes ?? [], color);
      } else {
        for (const p of pointsRef.current) {
          L.circleMarker(p, { radius: 4, color, fillColor: color, fillOpacity: 1 }).addTo(layer);
        }
      }
    };
    redrawRef.current = redraw;
    if (seed) redraw();

    const onClick = (e: L.LeafletMouseEvent) => {
      // Corners are edited through their handles; a map click lays nothing.
      if (modeRef.current === 'edit') return;
      pointsRef.current = [...pointsRef.current, [e.latlng.lat, e.latlng.lng]];
      setDrawPoints(pointsRef.current);
      redraw();
    };
    const onMove = (e: L.LeafletMouseEvent) => {
      // A redraw on every move would rebuild the handles under a drag in progress.
      if (modeRef.current === 'edit') return;
      if (pointsRef.current.length >= 1) redraw([e.latlng.lat, e.latlng.lng]);
    };
    map.on('click', onClick);
    map.on('mousemove', onMove);
    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMove);
      layer.remove();
      map.getContainer().style.cursor = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingZoneId]);

  const saveBoundary = useMutation({
    /**
     * The ring as DRAWN goes up, not the previewed clip: the server clips
     * against the topology as it is at that instant, and this map is judging
     * against one it loaded some time ago. Its sentence is the one that counts,
     * which is why it comes back and is shown.
     */
    mutationFn: async (): Promise<{ notice: string | null }> => {
      const id = encodeURIComponent(drawingZoneId!);
      if (drawMode === 'cut') {
        const r = await api.post<CutOutResponse>(`/zones/${id}/cut-out`, {
          ring: drawPoints,
          into: cutInto === 'zone' ? { name: cutName.trim() } : null,
        });
        return { notice: r.created ? `${r.notice ?? ''} ${r.created.name} is now a zone of its own.`.trim() : r.notice };
      }
      if (drawMode === 'split') {
        const keep = splitKeepName.trim();
        const r = await api.post<SplitResponse>(`/zones/${id}/split`, {
          line: drawPoints,
          newName: splitName.trim(),
          ...(keep.length > 0 ? { keepName: keep } : {}),
        });
        const moved =
          r.splittersMoved === 0
            ? 'No splitters moved.'
            : `${r.splittersMoved} splitter${r.splittersMoved === 1 ? '' : 's'} moved to ${r.created.name}.`;
        return { notice: `${r.notice ?? ''} ${r.zone.name} keeps one half and ${r.created.name} is the other. ${moved}`.trim() };
      }
      // Edited corners are a redraw of the whole ring under the push rule —
      // `drawPoints` IS the edited ring — so the API needs no mode of its own.
      const zone = await api.patch<Zone>(`/zones/${id}`, { boundary: drawPoints, boundaryMode: drawMode === 'edit' ? 'push' : drawMode });
      const moved = zone.neighboursMoved ?? [];
      const who = moved.length > 0 ? `Moved: ${moved.map((m) => `${m.name} (${m.change})`).join(', ')}.` : null;
      return { notice: [zone.boundaryNotice ?? null, who].filter(Boolean).join(' ') || null };
    },
    onSuccess: async (result) => {
      setSavedNotice(result.notice);
      await queryClient.invalidateQueries({ queryKey: ['topology'] });
      onDrawExit();
    },
  });

  const finished = drawPoints.length >= (drawMode === 'split' ? 2 : 3) ? judgeCandidate(drawPoints) : null;
  const canFinish =
    finished !== null &&
    finished.boundary !== null &&
    !(drawMode === 'cut' && cutInto === 'zone' && cutName.trim().length === 0) &&
    !(drawMode === 'split' && (finished.split === null || splitName.trim().length === 0));

  /**
   * A mode change re-judges the ring already on the map (the judge in the ref
   * sees the new mode on the next redraw). Entering "Edit corners" loads the
   * SAVED ring as the ring to edit and parks whatever corners were drawn so
   * far; leaving it hands those corners back — an operator who reached for
   * the wrong button loses nothing either way.
   */
  const chooseMode = (mode: DrawMode) => {
    if (mode === drawMode) return;
    if (mode === 'edit') {
      parkedRef.current = pointsRef.current;
      pointsRef.current = [...((drawingZone?.boundary ?? []) as [number, number][])];
    } else if (drawMode === 'edit') {
      pointsRef.current = parkedRef.current;
    }
    historyRef.current = [];
    setEditHistoryLen(0);
    setDrawPoints(pointsRef.current);
    modeRef.current = mode;
    setDrawMode(mode);
    // Handles are for dragging, not laying corners, and the cursor says which.
    const container = mapRef.current?.getContainer();
    if (container) container.style.cursor = mode === 'edit' ? '' : 'crosshair';
    setTimeout(() => redrawRef.current(), 0);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
          <input
            className="input h-8 w-64 pl-8 text-xs"
            placeholder="Search the map — town, road, landmark…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results.length > 0 && setListOpen(true)}
            onBlur={() => setListOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setQ('');
                setListOpen(false);
              }
            }}
          />
          {searching && (
            <Spinner className="absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2" />
          )}
          {listOpen && (
            <div className="absolute left-0 top-full z-[700] mt-1 w-[22rem] max-w-[80vw] overflow-hidden rounded-md border border-line bg-surface shadow-lg">
              {results.map((r, i) => (
                <button
                  key={`${r.lat},${r.lng},${i}`}
                  type="button"
                  className="block w-full truncate px-3 py-1.5 text-left text-xs text-ink hover:bg-surface-2"
                  title={r.label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    chooseResult(r);
                  }}
                >
                  {r.label}
                </button>
              ))}
              {searchNote && <p className="px-3 py-1.5 text-xs text-muted">{searchNote}</p>}
            </div>
          )}
        </div>
        {canManage && (
          <Button
            size="sm"
            variant={pinArmed ? 'primary' : 'ghost'}
            icon={<MapPin className="size-3.5" />}
            disabled={drawingZoneId !== null}
            onClick={() => {
              if (pin) {
                setPin(null);
                setPinArmed(false);
              } else {
                setPinArmed((armed) => !armed);
              }
            }}
            title={
              pin
                ? 'Remove the start pin'
                : 'Drop a pin where the zone starts — drawing begins from it'
            }
          >
            {pin ? 'Clear pin' : pinArmed ? 'Click the map…' : 'Drop a pin'}
          </Button>
        )}
        {pin && !drawingZoneId && (
          <span className="text-[11px] text-muted">
            Pin at {pin[0].toFixed(5)}, {pin[1].toFixed(5)} — pick a zone under “Draw a
            boundary” to start from it.
          </span>
        )}
      </div>
      {savedNotice && (
        <div className="flex items-start gap-2 border-b border-line bg-warn-soft px-4 py-2 text-[11px] font-medium text-warn">
          <span className="flex-1">Boundary saved. {savedNotice}</span>
          <button
            type="button"
            className="shrink-0 hover:opacity-70"
            onClick={() => setSavedNotice(null)}
            aria-label="Dismiss"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}
      <div className="relative">
      {drawingZone && (
        <div className="absolute left-2 top-2 z-[500] max-w-[calc(100%-1rem)] rounded-lg border border-line bg-surface p-3 shadow-lg">
          <p className="text-xs font-semibold text-ink">
            {drawMode === 'cut'
              ? `Cutting out of ${drawingZone.name} — click the map to outline the piece`
              : drawMode === 'edit'
                ? `Editing the corners of ${drawingZone.name} — drag a corner to move it, drag a midpoint to add one, right-click a corner to remove it`
                : drawMode === 'split'
                  ? `Splitting ${drawingZone.name} — click the map to lay a line across it; each side becomes a zone`
                  : `Drawing ${drawingZone.name} — click the map to lay each corner`}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {(
              [
                ['push', 'Neighbours move', 'The ring you draw is stored as drawn; neighbours are pushed back where you cover them and pulled forward where you release ground beside them.'],
                ['clip', 'Keep neighbours', 'Your ring yields: it is clipped to the neighbours it overlaps.'],
                ['cut', 'Cut out', 'Take a piece out of this zone — as empty space, or as a new zone with exactly that shape.'],
                ['edit', 'Edit corners', 'Move, add or remove the corners of the saved boundary instead of drawing it again; neighbours move as under "Neighbours move".'],
                ['split', 'Split', 'Draw a line across this zone: it keeps one half and a new zone is made of the other. Splitters standing in the new half go with it.'],
              ] as const
            ).map(([mode, label, title]) => {
              // Cutting out of, editing and splitting a ring all need one to exist.
              const needsRing = (mode === 'cut' || mode === 'edit' || mode === 'split') && !drawingZone.boundary;
              const noRing =
                mode === 'cut'
                  ? 'This zone has no boundary to cut out of yet'
                  : mode === 'split'
                    ? 'This zone has no boundary to split yet'
                    : 'This zone has no boundary to edit yet';
              return (
                <Button
                  key={mode}
                  size="sm"
                  variant={drawMode === mode ? 'primary' : 'ghost'}
                  disabled={needsRing}
                  title={needsRing ? noRing : title}
                  onClick={() => chooseMode(mode)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          {drawMode === 'cut' && (
            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px] text-muted">
              <label className="flex cursor-pointer items-center gap-1.5">
                <input type="radio" name="cut-into" checked={cutInto === 'empty'} onChange={() => setCutInto('empty')} />
                Empty space
              </label>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input type="radio" name="cut-into" checked={cutInto === 'zone'} onChange={() => setCutInto('zone')} />
                A new zone named
              </label>
              <input
                className="input h-7 w-40 text-xs"
                value={cutName}
                placeholder="Zone name"
                disabled={cutInto !== 'zone'}
                onChange={(e) => setCutName(e.target.value)}
              />
            </div>
          )}
          {drawMode === 'split' && (
            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px] text-muted">
              <label className="flex items-center gap-1.5">
                The other half becomes
                <input
                  className="input h-7 w-40 text-xs"
                  value={splitName}
                  placeholder="New zone name"
                  onChange={(e) => setSplitName(e.target.value)}
                />
              </label>
              <label className="flex items-center gap-1.5">
                Rename the half {drawingZone.name} keeps
                <input
                  className="input h-7 w-40 text-xs"
                  value={splitKeepName}
                  placeholder={`${drawingZone.name} (unchanged)`}
                  onChange={(e) => setSplitKeepName(e.target.value)}
                />
              </label>
            </div>
          )}
          <p className="mt-0.5 text-[11px] text-muted">
            {drawMode === 'edit' ? (
              <>
                {drawPoints.length} corner{drawPoints.length === 1 ? '' : 's'} · {editHistoryLen} change
                {editHistoryLen === 1 ? '' : 's'} · at least 3 stay
              </>
            ) : drawMode === 'split' ? (
              <>
                {drawPoints.length} point{drawPoints.length === 1 ? '' : 's'} · at least 2 to finish · the line is
                extended across the zone
                {seededFromPin && ' · point 1 is your pin'}
              </>
            ) : (
              <>
                {drawPoints.length} point{drawPoints.length === 1 ? '' : 's'} · at least 3 to finish
                {seededFromPin && ' · corner 1 is your pin'}
              </>
            )}
          </p>
          {/* The clip is not a failure, so it does not read like one: amber says
              the saved shape differs from the drawn one, red says nothing can
              be saved. */}
          {preview?.notice && (
            <p className="mt-1 max-w-sm text-[11px] font-medium text-warn">{preview.notice}</p>
          )}
          {preview?.error && (
            <p className="mt-1 max-w-sm text-[11px] font-medium text-danger">{preview.error}</p>
          )}
          {saveBoundary.error && (
            <p className="mt-1 text-[11px] font-medium text-danger">
              {saveBoundary.error instanceof Error ? saveBoundary.error.message : 'Could not save'}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="primary" disabled={!canFinish} loading={saveBoundary.isPending} onClick={() => saveBoundary.mutate()}>
              {drawMode === 'cut' ? (cutInto === 'zone' ? 'Cut out as a zone' : 'Cut out') : drawMode === 'split' ? 'Split' : 'Finish'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={drawMode === 'edit' ? editHistoryLen === 0 : drawPoints.length === 0}
              onClick={() => {
                if (drawMode === 'edit') {
                  // Walk back the last corner edit — the ring as it was before it.
                  const before = historyRef.current.pop();
                  if (!before) return;
                  setEditHistoryLen(historyRef.current.length);
                  pointsRef.current = before;
                } else {
                  pointsRef.current = pointsRef.current.slice(0, -1);
                }
                setDrawPoints(pointsRef.current);
                redrawRef.current();
              }}
              title={drawMode === 'edit' ? 'Undo the last corner edit' : 'Remove the last corner'}
            >
              Undo
            </Button>
            <Button size="sm" variant="ghost" onClick={onDrawExit}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {drawingRoute && (
        <div className="absolute left-2 top-2 z-[500] max-w-[calc(100%-1rem)] rounded-lg border border-line bg-surface p-3 shadow-lg">
          <p className="text-xs font-semibold text-ink">
            Drawing a fibre route — click the splitter the cable leaves, lay its bends along the run, click the
            splitter it reaches
          </p>
          <p className="mt-1.5 text-[11px] text-muted">
            <span className="text-ink">From:</span>{' '}
            {routeEnds.from === null
              ? `click a splitter marker — a click within ${ROUTE_SNAP_M} m of one lands on it`
              : routeEnds.from === 'olt'
                ? `no marker within ${ROUTE_SNAP_M} m of your first click, so the route starts at the OLT`
                : `${routeEnds.from.name} (snapped)`}
            {routeEnds.from === 'olt' && (
              <select
                className="input ml-2 h-7 w-44 text-xs"
                value={routeOltId}
                onChange={(e) => setRouteOltId(e.target.value)}
                title="The chassis has no position on the map, so this hop starts where you clicked"
              >
                <option value="">Which OLT…</option>
                {olts.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
          </p>
          <p className="mt-0.5 text-[11px] text-muted">
            <span className="text-ink">To:</span>{' '}
            {routeEnds.to
              ? `${routeEnds.to.name} (snapped)`
              : `end on a splitter marker — within ${ROUTE_SNAP_M} m of it`}
          </p>
          <p className="mt-0.5 text-[11px] text-muted">
            {routePoints.length} point{routePoints.length === 1 ? '' : 's'} ·{' '}
            <span className="text-ink">{formatDistance(routeLength)}</span> · +{fibreLossDb(routeLength).toFixed(1)} dB at{' '}
            {FIBRE_DB_PER_KM} dB/km
            {routeNear && <span className="text-warn"> · near {routeNear}</span>}
          </p>
          <input
            className="input mt-1.5 h-7 w-72 max-w-full text-xs"
            value={routeNote}
            placeholder="Note — duct, pole numbers, core colour (optional)"
            maxLength={500}
            onChange={(e) => setRouteNote(e.target.value)}
          />
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={!canFinishRoute}
              onClick={finishRoute}
              title={
                canFinishRoute
                  ? 'Store the route; the API measures its length from the points'
                  : routeEnds.from === 'olt' && routeOltId === ''
                    ? 'Choose the OLT the fibre leaves'
                    : 'End the route on a splitter marker first'
              }
            >
              Finish
            </Button>
            <Button size="sm" variant="ghost" disabled={routePoints.length === 0} onClick={undoRoutePoint} title="Remove the last point">
              Undo
            </Button>
            <Button size="sm" variant="ghost" onClick={onRouteExit}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div ref={containerRef} className="h-[32rem] w-full" />
      </div>
      {routeDeleting && (
        <ConfirmDeleteDialog
          title={`Delete the fibre route ${routeEndName(routeDeleting, 'from')} → ${routeEndName(routeDeleting, 'to')}?`}
          body={
            <p>
              The line leaves the map and {routeEndName(routeDeleting, 'to')} and everything below it lose the{' '}
              {formatDistance(routeDeleting.lengthM)} it measured. Nothing on the chassis changes.
            </p>
          }
          outcome={`Fibre route ${routeEndName(routeDeleting, 'from')} → ${routeEndName(routeDeleting, 'to')} deleted.`}
          onConfirm={async () => {
            await api.delete(`/fibre-routes/${encodeURIComponent(routeDeleting.id)}`);
            await queryClient.invalidateQueries({ queryKey: ['topology'] });
          }}
          onClose={() => setRouteDeleting(null)}
        />
      )}
    </div>
  );
}

// --- create / edit dialog ----------------------------------------------------

/** The stage after the parent's on the light path — the natural next tier. */
function nextKind(parent: SplitterKind | null): SplitterKind {
  if (!parent) return 'odf';
  if (parent === 'odf') return 'enclosure';
  return 'fat';
}

function UpsertDialog({
  kind,
  parentId,
  zoneId,
  existing,
  zones,
  splitters,
  olts,
  onClose,
}: {
  kind: 'zone' | 'splitter';
  parentId: string | null;
  zoneId: string | null;
  existing: Zone | Splitter | null;
  zones: Zone[];
  splitters: Splitter[];
  olts: Olt[];
  onClose: () => void;
}) {
  const tasks = useTasks();
  const editingSplitter = kind === 'splitter' && existing ? (existing as Splitter) : null;
  const parentSplitter =
    kind === 'splitter' && parentId ? (splitters.find((s) => s.id === parentId) ?? null) : null;
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    lat: existing?.lat != null ? String(existing.lat) : '',
    lng: existing?.lng != null ? String(existing.lng) : '',
    capacity: String(editingSplitter?.capacity ?? 8),
    splitterKind: editingSplitter?.kind ?? nextKind(parentSplitter?.kind ?? null),
    zoneId: zoneId ?? '',
    oltId: editingSplitter?.oltId ?? '',
    oltPonPort: editingSplitter?.oltPonPort ?? '',
    notes: editingSplitter?.notes ?? '',
  });
  const isMainSplitter = kind === 'splitter' && !parentId;
  const zoneDepths = useMemo(() => depthMap(zones), [zones]);

  // Ports the chosen OLT actually has, from its last SNMP sweep.
  const portsQuery = useQuery({
    queryKey: ['olt-pon-ports', form.oltId],
    queryFn: () =>
      api.get<{ items: PonPort[] }>(`/olts/${encodeURIComponent(form.oltId)}/pon-ports`),
    enabled: isMainSplitter && form.oltId !== '',
  });
  const ports = portsQuery.data?.items ?? [];

  /**
   * Submit closes the dialog at once and the write runs in the tasks tray;
   * the bodies are built here, not in the task, because the form is gone by
   * the time it runs.
   */
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const zoneBody = {
      name: form.name.trim(),
      lat: form.lat ? Number(form.lat) : null,
      lng: form.lng ? Number(form.lng) : null,
    };
    const splitterBody = {
      ...zoneBody,
      zoneId: form.zoneId || null,
      kind: form.splitterKind,
      capacity: Number(form.capacity) || 8,
      notes: form.notes.trim() || null,
      ...(isMainSplitter
        ? { oltId: form.oltId || null, oltPonPort: form.oltPonPort.trim() || null }
        : {}),
    };
    const noun = kind === 'zone' ? 'zone' : SPLITTER_KIND_LABELS[form.splitterKind];
    onClose();
    tasks.start({
      label: `${existing ? 'Save' : 'Create'} ${noun} ${zoneBody.name}`,
      run: () => {
        if (existing) {
          return kind === 'zone'
            ? api.patch(`/zones/${encodeURIComponent(existing.id)}`, zoneBody)
            : api.patch(`/splitters/${encodeURIComponent(existing.id)}`, splitterBody);
        }
        return kind === 'zone'
          ? api.post('/zones', { ...zoneBody, parentId })
          : api.post('/splitters', { ...splitterBody, parentId });
      },
      done: () => `${zoneBody.name} ${existing ? 'saved' : 'created'}.`,
      invalidate: [['topology']],
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {existing
              ? `Edit ${existing.name}`
              : kind === 'zone'
                ? parentId
                  ? 'New sub-zone'
                  : 'New zone'
                : parentId
                  ? 'New sub-splitter'
                  : 'New splitter'}
          </h2>
        </header>
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={kind === 'zone' ? 'Westlands' : 'FAT-WB-03'}
              required
              autoFocus
            />
          </Field>
          {kind === 'splitter' && (
            <>
              <Field label="Zone">
                <select
                  className="input"
                  value={form.zoneId}
                  onChange={(e) => setForm({ ...form, zoneId: e.target.value })}
                >
                  <option value="">No zone</option>
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {'— '.repeat(zoneDepths.get(zone.id) ?? 0)}
                      {zone.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Sits in" hint="OLT port → ODF → enclosure → FAT → ONU">
                <select
                  className="input"
                  value={form.splitterKind}
                  onChange={(e) =>
                    setForm({ ...form, splitterKind: e.target.value as SplitterKind })
                  }
                >
                  {(Object.keys(SPLITTER_KIND_LABELS) as SplitterKind[]).map((k) => (
                    <option key={k} value={k}>
                      {SPLITTER_KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Size" hint="One input, fanned out">
                <select
                  className="input"
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                >
                  {SPLITTER_CAPACITIES.map((size) => (
                    <option key={size} value={size}>
                      1×{size}
                    </option>
                  ))}
                </select>
              </Field>
              {isMainSplitter && (
                <>
                  <Field label="Fed from OLT" hint="Main splitters hang off a PON port">
                    <select
                      className="input"
                      value={form.oltId}
                      onChange={(e) => setForm({ ...form, oltId: e.target.value, oltPonPort: '' })}
                    >
                      <option value="">Not attached</option>
                      {olts.map((olt) => (
                        <option key={olt.id} value={olt.id}>
                          {olt.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="PON port">
                    {ports.length > 0 ? (
                      <select
                        className="input"
                        value={form.oltPonPort}
                        onChange={(e) => setForm({ ...form, oltPonPort: e.target.value })}
                        disabled={!form.oltId}
                      >
                        <option value="">Pick a port</option>
                        {ports.map((port) => (
                          <option key={port.id} value={port.port}>
                            {port.port} · {port.onuCount} ONUs
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={form.oltPonPort}
                        onChange={(e) => setForm({ ...form, oltPonPort: e.target.value })}
                        placeholder="1/2/1"
                        disabled={!form.oltId}
                      />
                    )}
                  </Field>
                </>
              )}
            </>
          )}
          <Field label="Latitude" hint="Optional — places it on the map">
            <Input
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              placeholder="-1.2385"
            />
          </Field>
          <Field label="Longitude">
            <Input
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              placeholder="36.7900"
            />
          </Field>
          {kind === 'splitter' && (
            <div className="sm:col-span-2">
              <Field
                label="Notes"
                hint="Fiber detail the field tech needs — core number/colour of the upstream feed, enclosure position"
              >
                <textarea
                  className="input min-h-16"
                  maxLength={500}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Fed from core 7 (white), tray 2"
                />
              </Field>
            </div>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {existing ? 'Save' : 'Create'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

// --- ONUs on a splitter -------------------------------------------------------

/**
 * Reading — who is on the splitter, who could be — happens here. Each WRITE
 * (assign the ticked ONUs, take one off, auto-assign from the PON port) closes
 * the dialog at once and runs in the tasks tray; the splitter's own list and
 * the topology refresh when it lands, and the operator reopens the dialog
 * for the next assignment rather than being held here through the write.
 */
function OnusDialog({ splitter, onClose }: { splitter: Splitter; onClose: () => void }) {
  const { can } = useSession();
  const tasks = useTasks();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const onusQuery = useQuery({
    queryKey: ['splitter-onus', splitter.id],
    queryFn: () => api.get<SplitterOnus>(`/splitters/${encodeURIComponent(splitter.id)}/onus`),
  });
  const data = onusQuery.data;
  // Occupied outputs are the greater of the assigned device rows and the ONU
  // ports actually taken — placement lives on the ONU, so an OMCI fleet has
  // taken ports with no device rows behind them (§8.2). Matches the server's
  // assign guard, so the "N outputs free" heading and the Assign gate agree
  // with what the API will accept.
  const used = Math.max(data?.assigned?.length ?? 0, data?.takenPorts?.length ?? 0);
  const free = (data?.capacity ?? splitter.capacity) - used;

  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const pool = data?.candidates ?? [];
    if (!needle) return pool;
    return pool.filter((onu) =>
      `${onu.alias ?? ''} ${onu.serial}`.toLowerCase().includes(needle),
    );
  }, [data, search]);

  const base = `/splitters/${encodeURIComponent(splitter.id)}/onus`;
  const invalidate = [['splitter-onus', splitter.id], ['topology']];

  const assign = () => {
    const deviceIds = [...selected];
    onClose();
    tasks.start({
      label: `Assign ${deviceIds.length} ONU(s) to ${splitter.name}`,
      run: () => api.post(base, { deviceIds }),
      done: () =>
        `${deviceIds.length} ONU${deviceIds.length === 1 ? '' : 's'} put on ${splitter.name}.`,
      invalidate,
    });
  };

  const remove = (onu: { id: string; alias: string | null; serial: string }) => {
    const name = onu.alias || onu.serial;
    onClose();
    tasks.start({
      label: `Take ${name} off ${splitter.name}`,
      run: () => api.delete(`${base}/${encodeURIComponent(onu.id)}`),
      done: () => `${name} taken off ${splitter.name}.`,
      invalidate,
    });
  };

  const autoAssign = () => {
    onClose();
    tasks.start<{ matched: number; assigned: number }>({
      label: `Auto-assign ONUs on PON ${splitter.oltPonPort} to ${splitter.name}`,
      run: () => api.post<{ matched: number; assigned: number }>(`${base}/auto`, {}),
      done: (result) =>
        result.assigned > 0
          ? `Assigned ${result.assigned} ONU(s) seen on PON port ${splitter.oltPonPort} to ${splitter.name}.`
          : `No unassigned ONUs seen on PON port ${splitter.oltPonPort}.`,
      invalidate,
    });
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">ONUs on {splitter.name}</h2>
            <p className="mt-0.5 text-xs text-muted">
              Each output carries one drop — assigning here also files the ONU under the
              splitter&apos;s zone.
            </p>
          </div>
          <Pill state tone={free <= 0 ? 'danger' : 'success'}>
            {used}/{data?.capacity ?? splitter.capacity} used
          </Pill>
        </header>

        {onusQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner className="size-5" />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <section className="px-5 py-4">
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">
                Assigned
              </h3>
              {data && data.assigned.length > 0 ? (
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {data.assigned.map((onu) => (
                    <li key={onu.id} className="flex items-center gap-3 px-3 py-2">
                      <Pill state
                        tone={
                          onu.status === 'online'
                            ? 'success'
                            : onu.status === 'never_seen'
                              ? 'warn'
                              : 'danger'
                        }
                      >
                        {onu.status.replace('_', ' ')}
                      </Pill>
                      <Link
                        to={onuPath(onu)}
                        className="min-w-0 flex-1 truncate text-sm text-ink hover:text-link"
                      >
                        {onu.alias || onu.serial}
                      </Link>
                      <span className="font-mono text-xs text-faint">
                        {onu.rxPowerDbm != null ? `${onu.rxPowerDbm.toFixed(1)} dBm` : '—'}
                      </span>
                      {can('topology.manage') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Take off this splitter"
                          icon={<X className="size-3.5" />}
                          onClick={() => remove(onu)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-faint">No ONUs on this splitter yet.</p>
              )}
            </section>

            {can('topology.manage') && (
              <section className="border-t border-line px-5 py-4">
                <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">
                  Assign ONUs {free > 0 ? `(${free} output${free === 1 ? '' : 's'} free)` : ''}
                </h3>
                {splitter.oltPonPort && free > 0 && (
                  <div className="mb-3">
                    <Button size="sm" icon={<Radio className="size-3.5" />} onClick={autoAssign}>
                      Auto-assign ONUs on PON {splitter.oltPonPort}
                    </Button>
                  </div>
                )}
                {free <= 0 ? (
                  <p className="text-sm text-warn">
                    Every output is used — remove an ONU or grow the splitter first.
                  </p>
                ) : (
                  <>
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Filter by name or serial…"
                    />
                    <ul className="mt-2 max-h-48 divide-y divide-line overflow-y-auto rounded-lg border border-line">
                      {candidates.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-faint">
                          No unassigned ONUs in this organization.
                        </li>
                      ) : (
                        candidates.map((onu) => (
                          <li key={onu.id}>
                            <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-2">
                              <input
                                type="checkbox"
                                checked={selected.has(onu.id)}
                                onChange={() => toggle(onu.id)}
                              />
                              <span className="min-w-0 flex-1 truncate text-sm text-ink">
                                {onu.alias || onu.serial}
                              </span>
                              <span className="font-mono text-xs text-faint">{onu.serial}</span>
                              <Pill state
                                tone={
                                  onu.status === 'online'
                                    ? 'success'
                                    : onu.status === 'never_seen'
                                      ? 'warn'
                                      : 'danger'
                                }
                              >
                                {onu.status.replace('_', ' ')}
                              </Pill>
                            </label>
                          </li>
                        ))
                      )}
                    </ul>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-faint">
                        {selected.size} selected
                        {selected.size > free ? ` — only ${free} free` : ''}
                      </span>
                      <Button
                        variant="primary"
                        disabled={selected.size === 0 || selected.size > free}
                        onClick={assign}
                      >
                        Assign selected
                      </Button>
                    </div>
                  </>
                )}
              </section>
            )}
          </div>
        )}

        <footer className="flex justify-end border-t border-line px-5 py-3">
          <Button onClick={onClose}>Close</Button>
        </footer>
      </div>
    </div>
  );
}

// --- delete confirmation ------------------------------------------------------

/**
 * Deleting plant records is the one topology action with no undo, so the
 * trash icon only opens this dialog and the button stays dead until the
 * operator retypes the exact name — a slip of the mouse cannot take out an
 * enclosure.
 */
function DeleteDialog({ spec, onClose }: { spec: DeleteSpec; onClose: () => void }) {
  const tasks = useTasks();
  const [typed, setTyped] = useState('');

  const target = spec.kind === 'zone' ? spec.zone : spec.splitter;
  const label = spec.kind === 'zone' ? 'zone' : SPLITTER_KIND_LABELS[spec.splitter.kind];
  const attached =
    spec.kind === 'zone'
      ? [
          spec.zone.splitterCount ? `${spec.zone.splitterCount} splitter(s)` : null,
          spec.zone.deviceCount ? `${spec.zone.deviceCount} ONU(s)` : null,
        ].filter(Boolean)
      : [spec.splitter.deviceCount ? `${spec.splitter.deviceCount} ONU(s)` : null].filter(Boolean);
  const nameMatches = typed.trim().toLowerCase() === target.name.trim().toLowerCase();

  // The retyped name is the gate; once it matches the dialog closes and the
  // delete runs in the tray, where a refusal ("still carries 3 ONUs") stays
  // until it is read.
  const remove = () => {
    onClose();
    tasks.start({
      label: `Delete ${label} ${target.name}`,
      run: () =>
        spec.kind === 'zone'
          ? api.delete(`/zones/${encodeURIComponent(spec.zone.id)}`)
          : api.delete(`/splitters/${encodeURIComponent(spec.splitter.id)}`),
      done: () => `${target.name} deleted.`,
      invalidate: [['topology']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (nameMatches) remove();
        }}
        className="relative w-full max-w-md rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Trash2 className="size-4 text-danger" />
            Delete {label} {target.name}
          </h2>
        </header>
        <div className="space-y-4 p-5">
          <p className="text-sm text-muted">
            This removes the {label} from the tree and the map. There is no undo.
          </p>
          {attached.length > 0 && (
            <p className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-sm text-warn">
              Still carries {attached.join(' · ')} — the delete will be refused until they are
              moved.
            </p>
          )}
          <Field label={`Type "${target.name}" to confirm`}>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={target.name}
              autoFocus
            />
          </Field>
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={!nameMatches}>
            Delete {label}
          </Button>
        </footer>
      </form>
    </div>
  );
}
